<template>
  <div class="app-shell">
    <router-view></router-view>
    <ThemeToggle/>
    <LanguageSelect/>
  </div>
</template>

<script>
import { t } from "@/i18n";
import axios from "axios";
import * as qiniu from "qiniu-js";
import { refreshToken, ensureValidAccessToken } from "@/utils/refreshToken";
import LanguageSelect from "@/components/LanguageSelect";
import ThemeToggle from "@/components/ThemeToggle";

let socketTask = null;
let reconnectTimeout = null;
let shouldReconnect = false;
let hadError = false;
let currentToken = null;
const BrowserWebSocket = typeof window !== "undefined"
    ? window.WebSocket || window.MozWebSocket
    : null;
const IMAGE_UPLOAD_MAX_DIMENSION = 1920;
const IMAGE_UPLOAD_TARGET_BYTES = 1024 * 1024;
const IMAGE_UPLOAD_QUALITIES = [0.82, 0.74, 0.66, 0.58];

function shouldCompressImage(file) {
  if (!file || !file.type || !file.type.startsWith('image/')) {
    return false;
  }
  return !['image/gif', 'image/svg+xml'].includes(file.type);
}

function getCompressedFileName(fileName) {
  const fallbackName = `image-${Date.now()}.jpg`;
  if (!fileName || typeof fileName !== 'string') {
    return fallbackName;
  }
  return fileName.replace(/\.[^.]+$/, '') + '.jpg';
}

function createImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = (error) => {
      URL.revokeObjectURL(objectUrl);
      reject(error);
    };
    image.src = objectUrl;
  });
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error(t("图片压缩失败")));
      }
    }, type, quality);
  });
}

async function compressImageFile(file) {
  if (!shouldCompressImage(file) || file.size <= IMAGE_UPLOAD_TARGET_BYTES) {
    return file;
  }

  const image = await createImageFromFile(file);
  const scale = Math.min(1, IMAGE_UPLOAD_MAX_DIMENSION / Math.max(image.width, image.height));
  const width = Math.max(1, Math.round(image.width * scale));
  const height = Math.max(1, Math.round(image.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) {
    return file;
  }
  context.drawImage(image, 0, 0, width, height);

  let smallestBlob = null;
  for (const quality of IMAGE_UPLOAD_QUALITIES) {
    const blob = await canvasToBlob(canvas, 'image/jpeg', quality);
    if (!smallestBlob || blob.size < smallestBlob.size) {
      smallestBlob = blob;
    }
    if (blob.size <= IMAGE_UPLOAD_TARGET_BYTES) {
      smallestBlob = blob;
      break;
    }
  }

  if (!smallestBlob || smallestBlob.size >= file.size) {
    return file;
  }
  if (typeof File === 'function') {
    return new File([smallestBlob], getCompressedFileName(file.name), {
      type: smallestBlob.type,
      lastModified: Date.now()
    });
  }
  smallestBlob.name = getCompressedFileName(file.name);
  return smallestBlob;
}

export default {
  name: 'App',
  components: {
    ThemeToggle,
    LanguageSelect,
  },
  url: process.env.VUE_APP_BASE_URL,
  methods: {
    async connectWebSocket() {
      let accessToken;
      try {
        accessToken = await ensureValidAccessToken();
      } catch (e) {
        console.error("Failed to ensure access token before WebSocket connection:", e);
        return;
      }
      if (!accessToken) {
        return;
      }

      if (!BrowserWebSocket) {
        console.warn("WebSocket is not supported in this environment.");
        return;
      }

      if (socketTask) {
        if (currentToken === accessToken && socketTask.readyState === BrowserWebSocket.OPEN) {
          return;
        }
        const oldSocket = socketTask;
        socketTask = null;
        oldSocket.onclose = null;
        oldSocket.close();
      }

      shouldReconnect = true;
      hadError = false;
      currentToken = accessToken;
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
        reconnectTimeout = null;
      }
      const url = `wss://${location.host}${process.env.VUE_APP_BASE_URL}/ws/notifications?token=${encodeURIComponent(accessToken)}`;
      socketTask = new BrowserWebSocket(url);
      socketTask.onmessage = (msg) => {
        console.log("WebSocket received:", msg);
        console.log("WebSocket data:", msg.data);
        if (msg.data === "PING") {
          socketTask.send("PONG");
          return;
        }
        try {
          const data = JSON.parse(msg.data);
          if (data && data.type === "NOTIFICATION_COUNT") {
            const unreadCount = data.count;
            console.log("WebSocket parsed data:", unreadCount);
          } else if (data && data.type === "EXPORT_PROGRESS") {
            // Notify listeners about export progress updates
            const progress = Number(data.progress);
            window.dispatchEvent(
                new CustomEvent('export-progress', {detail: progress})
            );
          } else {
            console.log("Unsupported WebSocket message:", data);
          }
        } catch (e) {
          console.log("Failed to parse WebSocket message:", msg.data);
        }
      };
      socketTask.onerror = (err) => {
        console.log("WebSocket error:", err);
        hadError = true;
        socketTask.close();
      };
      socketTask.onclose = async () => {
        socketTask = null;
        currentToken = null;
        if (shouldReconnect) {
          if (hadError) {
            try {
              await refreshToken();
            } catch (refreshErr) {
              console.error("Failed to refresh token:", refreshErr);
              shouldReconnect = false;
              return;
            }
          }
          reconnectTimeout = setTimeout(() => {
            this.connectWebSocket();
          }, 5000);
        }
      };
    },
    closeWebSocket() {
      shouldReconnect = false;
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
        reconnectTimeout = null;
      }
      if (socketTask) {
        const oldSocket = socketTask;
        socketTask = null;
        currentToken = null;
        oldSocket.onclose = null;
        oldSocket.close();
      }
    },
    getEmployees(companyId) {
      return axios
          .get(process.env.VUE_APP_BASE_URL + `/companies/${companyId}/employees`, {
          })
          .then((response) => {
            return response.data.records;
          });
    },
    getEmployeesPage(companyId, cursor, limit = 20) {
      return axios
          .get(process.env.VUE_APP_BASE_URL + `/companies/${companyId}/employees`, {
            params: {
              cursorScore: cursor && cursor.nextCursorScore,
              cursorUserId: cursor && cursor.nextCursorUserId,
              limit,
            },
          })
          .then((response) => response.data || {});
    },
    getCompanyUserStats(companyId) {
      return axios
          .get(process.env.VUE_APP_BASE_URL + `/companies/${companyId}/users/stats`)
          .then((response) => response.data || {});
    },
    searchEmployees(name) {
      return axios.get(process.env.VUE_APP_BASE_URL + '/companies/current/users', {
        params: { name }
      });
    },

    updateIssue(issueId, departmentId, onSuccess) {
      return axios
          .put(`${process.env.VUE_APP_BASE_URL}/issues/${issueId}`, {
            departmentId: departmentId,
          })
          .then(() => {
            if (typeof onSuccess === 'function') {
              onSuccess();
            }
          });
    },

    getIssueTypes() {
      return axios.get(process.env.VUE_APP_BASE_URL + '/issueType/getIssueTypes')
    },

    getAllDepartmentsOfCompany(companyId, parentId, includeRoot = false) {
      let url = process.env.VUE_APP_BASE_URL + `/companies/${companyId}/departments`;
      const queryParams = [];
      if (typeof parentId !== 'undefined') {
        queryParams.push(`parentId=${parentId}`);
      }
      if (includeRoot) {
        queryParams.push('includeRoot=true');
      }
      if (queryParams.length > 0) {
        url += `?${queryParams.join('&')}`;
      }
      return axios.get(url);
    },

    setEmployeeDepartment(userId, departmentId) {
      const patchBody = [
        {
          op: 'replace',
          path: '/departmentId',
          value: departmentId
        }
      ];
      return axios.patch(
          `${process.env.VUE_APP_BASE_URL}/users/${userId}`,
          patchBody,
          {
            headers: {
              'Content-Type': 'application/json-patch+json'
            }
          }
      );
    },
    updateUserProfile(userId, payload = {}) {
      const patchBody = [];
      if (Object.prototype.hasOwnProperty.call(payload, 'name')) {
        patchBody.push({
          op: 'replace',
          path: '/name',
          value: payload.name
        });
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'phone')) {
        patchBody.push({
          op: 'replace',
          path: '/phone',
          value: payload.phone === '' ? null : payload.phone
        });
      }
      if (patchBody.length === 0) {
        return Promise.resolve();
      }
      return axios.patch(
          `${process.env.VUE_APP_BASE_URL}/users/${userId}`,
          patchBody,
          {
            headers: {
              'Content-Type': 'application/json-patch+json'
            }
          }
      );
    },

    getActivities(companyId) {
      return axios
          .get(`${process.env.VUE_APP_BASE_URL}/activities`, {
            params: {
              sortBy: 'publish_date',
              ascOrDesc: 'desc',
              pageNum: 0,
              pageSize: 1000,
              companyId,
            },
          })
          .then((res) => {
            return res.data.records;
          });
    },

    async uploadFile(file) {
      let uploadFile = file;
      try {
        uploadFile = await compressImageFile(file);
      } catch (error) {
        console.warn('compress image failed, upload original file instead:', error);
      }
      const { data: uploadParams = {} } = await axios.post(
          process.env.VUE_APP_BASE_URL + '/qiniu/getUploadToken'
      );

      const domain = typeof uploadParams.domain === 'string' ? uploadParams.domain : '';
      const token = uploadParams.uploadToken || uploadParams.token;

      return new Promise((resolve, reject) => {
        if (!token) {
          reject(new Error(t("缺少上传凭证")));
          return;
        }

        const observable = qiniu.upload(uploadFile, undefined, token, {}, { useCdnDomain: true });
        observable.subscribe({
          next() {},
          error(err) {
            reject(err);
          },
          complete(res) {
            const baseDomain = domain.endsWith('/') || !domain ? domain : `${domain}/`;
            resolve(`${baseDomain}${res.key}`);
          }
        });
      });
    },
    getCountOfAllIssues(object, signal) {
      return axios.post(
        process.env.VUE_APP_BASE_URL + '/issues/getCountOfIssue',
        object || {},
        { signal: signal }
      )
    },
    getUser() {
      return axios.get(process.env.VUE_APP_BASE_URL + '/users/me')
    },
    getEmploymentRelationships(userId) {
      return axios.get(`${process.env.VUE_APP_BASE_URL}/users/${userId}/employment-relationships`);
    },
    async getIssues(object, cursor, signal) {
      const response = await axios.post(
          process.env.VUE_APP_BASE_URL + '/issues/getIssues',
          object,
          {
            params: {
              lastIssueId: cursor?.lastIssueId
            },
            signal: signal
          }
      );
      const payload = response.data || {};
      let issues = [];
      if (!payload.error) {
        issues = Array.isArray(payload.records) ? payload.records : [];
        for (let i = 0; i < issues.length; i++) {
          issues[i].issueStatusName = ['待解决', '待验收', '已验收'][issues[i].issueStatus];
          issues[i].tagType = ['danger', 'warning', 'success'][issues[i].issueStatus];
        }
      }
      return {
        records: issues,
        nextCursor: payload.nextCursor || null
      };
    },
    async switchCompany(companyId) {
      const res = await axios.post(
          `${process.env.VUE_APP_BASE_URL}/auth/switch-company`,
          { companyId }
      );
      const { accessToken, refreshToken: newRefreshToken } = res.data || {};
      if (accessToken && newRefreshToken) {
        localStorage.setItem("accessToken", accessToken);
        localStorage.setItem("refreshToken", newRefreshToken);
        axios.defaults.headers.common["Authorization"] = "Bearer " + accessToken;
        // Reconnect WebSocket so it uses the fresh token
        this.closeWebSocket();
        this.connectWebSocket();
      }
      return res.data;
    },
  }
}
</script>

<style>
@import "~weui";
</style>
